import { ProductProvider } from '$shared/utils/product';
import { usePriceAndStockCount } from '$shared/utils/product/hooks/use-price-and-stock-count';
import {useEffect, useMemo, useRef} from 'react';
import { IP50ProductDetailPage } from '~/lib/data-contract';
import { DynamicBlockList } from '~/templates/blocks';
import { StyledModulesWrapper } from '../P60ModulePage/style';
import { PdpCore } from './components/pdp-core';
import { PdpImageArea } from './components/pdp-image-area';
import { PdpSpecifications } from './components/pdp-specifications';
import { PdpTabs } from './components/pdp-tabs';
import {
    StyledPdPGridData,
    StyledPdPGrid,
    StyledPdPGridImage,
    StyledPdPGridBottomArea,
    StyledPdPSpecifications,
    StyledBorder, StyledImageWrapper,
} from './style';
import {pushEcommerceEvent} from "$services/piwik/ecommerce-tracking.service";

type P50ProductDetailPageProps = IP50ProductDetailPage;

export const P50ProductDetailPage = ({ pageElements, product, breadcrumb }: P50ProductDetailPageProps) => {
    const originalItemNumber = product?.originalItemNumber || '';

    const { productPricesAndStockCounts, isBusy } = usePriceAndStockCount(
        ['P50ProductDetailPage', originalItemNumber],
        [originalItemNumber]
    );

    const breadcrumbCategories = useMemo((): string[] => {
        const items =
            breadcrumb?.items
                ?.map((b) => b.text || b.title)
                .filter((v): v is string => typeof v === "string" && v.length > 0)
            ?? [];

        return items.slice(0, -1);
    }, [breadcrumb]);

    const mapCategoriesToGA4 = (categories: string[]) => {
        const mapped: Record<string, string> = {};

        categories.forEach((cat, index) => {
            const key =
                index === 0
                    ? "item_category"
                    : `item_category${index + 1}`;

            mapped[key] = cat;
        });

        return mapped;
    };

    const priceAndStockCount = useMemo(() => productPricesAndStockCounts.get(originalItemNumber), [
        originalItemNumber,
        productPricesAndStockCounts,
    ]);

    const sentForKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!product) return;

        const itemId =
            product.originalItemNumber ||
            product.sanitizedItemNumber ||
            product.alternativeItemNumber ||
            product.id;

        if (sentForKeyRef.current === itemId) return;

        const buildEcommerce = () => {
            const hasPrice = !!priceAndStockCount?.prices?.length;
            const price = hasPrice
                ? priceAndStockCount?.prices?.[0]?.netPrice ??
                priceAndStockCount?.prices?.[0]?.grossPrice ??
                0
                : undefined;

            const ga4Categories = mapCategoriesToGA4(breadcrumbCategories);

            const ecommerce: any = {
                items: [
                    {
                        item_id: itemId,
                        item_name:
                            product.description1 ||
                            product.description2 ||
                            product.description3 ||
                            "Unknown product",
                        item_brand: "Kyocera",
                        quantity: 1,
                        ...ga4Categories,
                    },
                ],
            };

            if (hasPrice && price !== undefined) {
                ecommerce.currency = priceAndStockCount?.currency;
                ecommerce.value = price;
                ecommerce.items[0].price = price;
            }

            return ecommerce;
        };

        const hasPriceNow = !!priceAndStockCount?.prices?.length;

        const sendOnce = () => {
            if (sentForKeyRef.current === itemId) return;
            sentForKeyRef.current = itemId;
            pushEcommerceEvent("view_item", buildEcommerce());
        };

        if (hasPriceNow) {
            sendOnce();
            return;
        }

        const timeoutId = window.setTimeout(() => {
            sendOnce();
        }, 3000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [product, priceAndStockCount, breadcrumbCategories]);


    return (
        <ProductProvider {...product} {...priceAndStockCount} isFetchingPriceAndStockCount={isBusy}>
            <StyledPdPGrid>
                <StyledImageWrapper>
                    <StyledPdPGridImage>
                        <PdpImageArea/>
                    </StyledPdPGridImage>
                </StyledImageWrapper>
                <StyledPdPGridData>
                    <PdpCore/>
                    <StyledPdPSpecifications>
                        <StyledBorder>
                            <PdpSpecifications/>
                        </StyledBorder>
                    </StyledPdPSpecifications>
                </StyledPdPGridData>
                <StyledPdPGridBottomArea>
                    <PdpTabs/>
                </StyledPdPGridBottomArea>
            </StyledPdPGrid>
            <StyledModulesWrapper>
                <DynamicBlockList elements={pageElements} />
            </StyledModulesWrapper>
        </ProductProvider>
    );
};
